import { Tooltip as TooltipPrimitive } from 'radix-ui';
import styled, { css, keyframes } from 'styled-components';

import { Z_INDEX_MODAL } from '@savgroup-front-common/constants';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';

import { TOOLTIP_VARIANT } from './Tooltip.types';

const inFromBottom = keyframes`
  from { opacity: 0; transform: translateY(${spacing('sm')}) scale(0.95); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const inFromTop = keyframes`
  from { opacity: 0; transform: translateY(${rem(-8)}) scale(0.95); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const inFromLeft = keyframes`
  from { opacity: 0; transform: translateX(${rem(-8)}) scale(0.95); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;
const inFromRight = keyframes`
  from { opacity: 0; transform: translateX(${spacing('sm')}) scale(0.95); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
`;

const outToBottom = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(${spacing('sm')}) scale(0.95); }
`;
const outToTop = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(${rem(-8)}) scale(0.95); }
`;
const outToLeft = keyframes`
  from { opacity: 1; transform: translateX(0) scale(1); }
  to   { opacity: 0; transform: translateX(${rem(-8)}) scale(0.95); }
`;
const outToRight = keyframes`
  from { opacity: 1; transform: translateX(0) scale(1); }
  to   { opacity: 0; transform: translateX(${spacing('sm')}) scale(0.95); }
`;

const openAnimation = css`
  &[data-state='delayed-open'],
  &[data-state='instant-open'] {
    animation-duration: 150ms;
    animation-timing-function: ease-out;
    animation-fill-mode: both;
  }

  &[data-state='delayed-open'][data-side='top'],
  &[data-state='instant-open'][data-side='top'] {
    animation-name: ${inFromBottom};
  }

  &[data-state='delayed-open'][data-side='bottom'],
  &[data-state='instant-open'][data-side='bottom'] {
    animation-name: ${inFromTop};
  }

  &[data-state='delayed-open'][data-side='left'],
  &[data-state='instant-open'][data-side='left'] {
    animation-name: ${inFromRight};
  }

  &[data-state='delayed-open'][data-side='right'],
  &[data-state='instant-open'][data-side='right'] {
    animation-name: ${inFromLeft};
  }
`;

const closeAnimation = css`
  &[data-state='closed'] {
    animation-duration: 120ms;
    animation-timing-function: ease-in;
    animation-fill-mode: both;
  }

  &[data-state='closed'][data-side='top'] {
    animation-name: ${outToBottom};
  }
  &[data-state='closed'][data-side='bottom'] {
    animation-name: ${outToTop};
  }
  &[data-state='closed'][data-side='left'] {
    animation-name: ${outToRight};
  }
  &[data-state='closed'][data-side='right'] {
    animation-name: ${outToLeft};
  }
`;

export const $TooltipContent = styled(TooltipPrimitive.Content)`
  z-index: ${Z_INDEX_MODAL};
  width: fit-content;
  max-width: ${spacing(96)};
  border-radius: ${({ theme }) => theme.tw.radius.lg};
  padding: ${spacing('sm')} ${spacing('md')};

  background: ${({ theme }) => theme.colors.darkBackground};
  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 ${spacing('sm')} ${spacing('xl')} rgba(0, 0, 0, 0.2);

  &[data-variant='${TOOLTIP_VARIANT.WHITE}'] {
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.darkBackground};
    box-shadow: 0 ${spacing('sm')} ${spacing('xl')} rgba(0, 0, 0, 0.12);
  }

  transform-origin: var(--radix-tooltip-content-transform-origin);

  ${openAnimation}
  ${closeAnimation}
`;

export const $TooltipArrow = styled(TooltipPrimitive.Arrow)`
  width: ${spacing(2.5)};
  height: ${spacing(2.5)};
  border-radius: ${({ theme }) => theme.tw.radius.xs};
  transform: rotate(45deg) translateY(calc(-50% - ${spacing('xxs')}));

  background: ${({ theme }) => theme.colors.darkBackground};
  fill: ${({ theme }) => theme.colors.darkBackground};

  &[data-variant='${TOOLTIP_VARIANT.WHITE}'] {
    background: ${({ theme }) => theme.colors.white};
    fill: ${({ theme }) => theme.colors.white};
  }
`;
